import type { CarInput } from '../../shared/car.js';

/**
 * What the keyboard controller needs from a touch source to merge one in. `TouchControls`
 * (src/client/ui/touchControls.ts) satisfies this structurally — this file doesn't import
 * it, so a controller under test can hand in a plain object instead of building real DOM.
 */
export interface TouchInput {
  sample(): CarInput;
  readonly braking: boolean;
  readonly lookingBack: boolean;
  reset(): void;
}

/**
 * Keyboard input, optionally merged with a touch source. WASD and the arrow keys both
 * work; space is the handbrake and C looks back. Keys are tracked by `event.code` so the
 * controls are the same on a QWERTY and an AZERTY keyboard.
 *
 * The merge is additive rather than "touch wins" or "keyboard wins": a touchscreen laptop
 * with a keyboard attached should be able to use either, and on a phone the keyboard's
 * `held` set is simply always empty, so this reduces to the touch value alone.
 */
export class InputController {
  private readonly held = new Set<string>();
  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onBlur: () => void;

  /** Set by the game so a paused/finished race ignores the keyboard. */
  enabled = true;

  /**
   * Set while a menu is up.
   *
   * Deliberately a second flag rather than borrowing `enabled`: that one belongs to the
   * race's own state machine, which switches it on at the green light and off at the flag.
   * A menu that saved and restored it would put the wrong value back for anyone who opened
   * the menu during the countdown and closed it after the lights.
   */
  blocked = false;

  private get active(): boolean {
    return this.enabled && !this.blocked;
  }

  constructor(target: Window = window, private readonly touch: TouchInput | null = null) {
    this.onKeyDown = (e) => {
      if (e.repeat) return;
      // Don't hijack keys while someone is typing their name in the lobby.
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      if (TRACKED.has(e.code)) {
        this.held.add(e.code);
        e.preventDefault();
      }
    };
    this.onKeyUp = (e) => {
      this.held.delete(e.code);
    };
    // Losing focus mid-corner would otherwise leave the throttle pinned. `TouchControls`
    // guards the same hazard itself (a phone call doesn't blur the window the way alt-tab
    // does), but clearing here too costs nothing and covers whichever platform is wrong.
    this.onBlur = () => {
      this.held.clear();
      this.touch?.reset();
    };

    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.onBlur);
    this.target = target;
  }

  private readonly target: Window;

  get lookingBack(): boolean {
    return (
      this.active &&
      (this.held.has('KeyC') || this.held.has('ShiftLeft') || (this.touch?.lookingBack ?? false))
    );
  }

  get braking(): boolean {
    return (
      this.active &&
      (this.held.has('KeyS') || this.held.has('ArrowDown') || (this.touch?.braking ?? false))
    );
  }

  sample(): CarInput {
    if (!this.active) return { throttle: 0, steer: 0, handbrake: false };
    const up = this.held.has('KeyW') || this.held.has('ArrowUp');
    const down = this.held.has('KeyS') || this.held.has('ArrowDown');
    const left = this.held.has('KeyA') || this.held.has('ArrowLeft');
    const right = this.held.has('KeyD') || this.held.has('ArrowRight');
    const keyboard: CarInput = {
      throttle: (up ? 1 : 0) - (down ? 1 : 0),
      // Positive steer is counter-clockwise seen from above, which is a left turn.
      steer: (left ? 1 : 0) - (right ? 1 : 0),
      handbrake: this.held.has('Space'),
    };
    if (!this.touch) return keyboard;
    const touch = this.touch.sample();
    return {
      throttle: clamp(keyboard.throttle + touch.throttle, -1, 1),
      steer: clamp(keyboard.steer + touch.steer, -1, 1),
      handbrake: keyboard.handbrake || touch.handbrake,
    };
  }

  clear(): void {
    this.held.clear();
    this.touch?.reset();
  }

  dispose(): void {
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
    this.held.clear();
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

const TRACKED = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'KeyC',
  'KeyR',
  'Space',
  'ShiftLeft',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
]);
